/**
 * PrintSizeCodeCombobox — Popover+Command pick-list cloned from the pattern
 * in `MetafieldPicker` (product-data-lookup), adapted for Print Size Rules'
 * product-type / size codes: surfaces the SELECTED CLIENT's own
 * product-sync code tables (`productSync.getCodeTablesForOrg`) as options
 * instead of asking the operator to re-type codes the client already
 * defined. See `architectural-decisions.md` § "Shared 3PL↔client data is
 * surfaced, never re-typed".
 *
 * SHARED PRIMITIVE — do not fork:
 *   - `PrintSizeRulesSection` (product-type + size codes, per client org),
 *   - `RipRoutingSection` (product-type + size codes, own org),
 *   - `DefaultWeightsSection` (product-type codes, per client org),
 *   - `YunExpressLocationSettings` (the carrier's shipping-product codes).
 * The `PrintSize` in the name is historical; it is the code pick-list for any
 * settings surface keyed by a list of codes someone else already defined.
 *
 * Two shapes, one control:
 *   - ADD (default): the trigger always shows the placeholder and every pick
 *     adds a row in the caller.
 *   - SINGLE VALUE: pass `value` and the trigger shows the current choice as
 *     `CODE · name`, like a Select. Pair it with `status` 'empty' / 'loaded'
 *     (never 'loading' / 'error'): the degraded Input+Add shape has no place
 *     to show a current value.
 *
 * Code grammar: by default typed text is UPPERCASED and must pass
 * `RIP_CODE_REGEX` to be offered as a new code. A vocabulary that is
 * case-sensitive or longer (a carrier's product codes such as `DHLCN-B`)
 * passes `preserveCase` and its own `isValidCode`. Matching against the
 * listed options is case-insensitive either way, so typing `thphr` finds
 * `THPHR` instead of offering it again as a new code.
 *
 * Free-text philosophy (mirrors MetafieldPicker — the dropdown is a
 * SHORTCUT, never a constraint): unmatched typed text always offers a
 * creatable "Add" row, and the whole combobox degrades to a plain
 * Input+Add button when the code-table query hasn't loaded or failed —
 * every code the write-path zod would accept stays reachable either way.
 *
 * Four states via `status` (mirrors `DiscoveryStatus`):
 *   - `loading` / `error` — degraded: plain Input + Add button (no popover).
 *     `error` also renders a muted one-line hint below.
 *   - `empty`   — popover shown, but `options` is empty — only the
 *     creatable row (plus the optional "No size" row) is offered.
 *   - `loaded`  — popover shown with the full curated option list.
 *
 * Selection ALWAYS flows through the caller's `onSelect` — an existing
 * option's code, the `PRINT_SIZE_NO_SIZE_KEY` sentinel (when
 * `allowNoSize`), or newly typed text (uppercased) — so the SAME
 * RIP_CODE_REGEX + dedupe + toast validators the free-text flow already
 * used keep gating every write, never duplicated in here.
 *
 * Every `<button>` carries `type="button"` explicitly (MetafieldPicker
 * lesson — this renders inside the Settings form).
 */
import { useState } from 'react';
import { ChevronsUpDown, Loader2, Plus } from 'lucide-react';
import { PRINT_SIZE_NO_SIZE_KEY, RIP_CODE_REGEX } from '@threadheads/shared';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface PrintSizeCodeOption {
  code: string;
  label: string;
  badges?: string[];
  disabled?: boolean;
  disabledReason?: string;
}

export type PrintSizeCodeComboboxStatus = 'loading' | 'error' | 'empty' | 'loaded';

export interface PrintSizeCodeComboboxProps {
  /** Curated codes to pick from (the client's own product-sync table). */
  options: PrintSizeCodeOption[];
  /** Prepend a "No size (default)" row resolving to `PRINT_SIZE_NO_SIZE_KEY`. */
  allowNoSize?: boolean;
  /**
   * Called with the resolved code — an existing option's code, the
   * `PRINT_SIZE_NO_SIZE_KEY` sentinel, or newly typed text (uppercased).
   * Return `true` to clear the input (and close the popover); `false`
   * leaves the input as-is so the operator can correct it.
   */
  onSelect: (code: string) => boolean;
  status: PrintSizeCodeComboboxStatus;
  placeholder: string;
  ariaLabel: string;
  addButtonLabel: string;
  /** Required when `allowNoSize` — the label for the sentinel row. */
  noSizeLabel?: string;
  createLabel: (code: string) => string;
  errorHint?: string;
  emptyLabel: string;
  className?: string;
  /** Id for the trigger (or the degraded Input), so a `<Label htmlFor>` points at it. */
  id?: string;
  /** Marks the control required for assistive tech; the caller draws the visible asterisk. */
  ariaRequired?: boolean;
  /** Space-separated ids of the caller's hint lines, read as the control's description. */
  ariaDescribedBy?: string;
  /**
   * SINGLE VALUE mode: the current choice, rendered on the trigger as
   * `CODE · label`. `null` renders the placeholder. Omit for ADD mode.
   */
  value?: { code: string; label?: string | null } | null;
  /** Keep typed text's case instead of uppercasing it. Default false. */
  preserveCase?: boolean;
  /**
   * Whether typed text may be offered as a new code. Defaults to
   * `RIP_CODE_REGEX`. Receives the trimmed (and, unless `preserveCase`,
   * uppercased) text. The caller's `onSelect` still gates every write.
   */
  isValidCode?: (code: string) => boolean;
  /**
   * Forwarded to the popover. Radix returns focus to the trigger when the
   * popover closes; a caller that moves focus elsewhere after a pick (to the
   * row it just added) calls `event.preventDefault()` here.
   */
  onCloseAutoFocus?: (event: Event) => void;
  /**
   * The trigger can't open (combobox mode only). The caller says why next to
   * it (a note named by `ariaDescribedBy`), e.g. there is nothing to pick.
   */
  disabled?: boolean;
}

const isRipCode = (code: string): boolean => RIP_CODE_REGEX.test(code);

export function PrintSizeCodeCombobox({
  options,
  allowNoSize = false,
  onSelect,
  status,
  placeholder,
  ariaLabel,
  addButtonLabel,
  noSizeLabel,
  createLabel,
  errorHint,
  emptyLabel,
  className,
  id,
  ariaRequired,
  ariaDescribedBy,
  value,
  preserveCase = false,
  isValidCode = isRipCode,
  onCloseAutoFocus,
  disabled = false,
}: PrintSizeCodeComboboxProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [fallbackText, setFallbackText] = useState('');

  const toCode = (raw: string): string => (preserveCase ? raw.trim() : raw.trim().toUpperCase());

  const commit = (code: string): void => {
    if (onSelect(code)) {
      setSearch('');
      setOpen(false);
    }
  };

  // ---- degraded: the query hasn't loaded / failed — plain Input+Add, ----
  // ---- exactly today's free-text behaviour, never blocks the operator. ----
  if (status === 'loading' || status === 'error') {
    const candidate = toCode(fallbackText);
    const handleAdd = (): void => {
      if (!candidate) return;
      if (onSelect(candidate)) setFallbackText('');
    };
    return (
      <div className="space-y-1">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            id={id}
            value={fallbackText}
            placeholder={placeholder}
            className={cn('h-11 font-mono sm:w-64', className)}
            aria-label={ariaLabel}
            aria-required={ariaRequired}
            aria-describedby={ariaDescribedBy}
            disabled={status === 'loading'}
            onChange={(e) => setFallbackText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd();
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            className="h-11 gap-1"
            disabled={status === 'loading' || !candidate}
            onClick={handleAdd}
          >
            {status === 'loading' ? (
              <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            ) : (
              <Plus className="h-4 w-4" aria-hidden="true" />
            )}
            {addButtonLabel}
          </Button>
        </div>
        {status === 'error' && errorHint && <p className="text-xs text-muted-foreground">{errorHint}</p>}
      </div>
    );
  }

  // ---- combobox: 'empty' (creatable row only) or 'loaded' (full list). ----
  const term = search.trim();
  const termCode = toCode(search);
  const needle = term.toUpperCase();
  const filtered = term
    ? options.filter(
        (o) => o.code.toUpperCase().includes(needle) || o.label.toLowerCase().includes(term.toLowerCase()),
      )
    : options;
  const exactMatch = options.some((o) => o.code.toUpperCase() === needle);
  const canCreate = term.length > 0 && !exactMatch && isValidCode(termCode);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-label={ariaLabel}
          aria-required={ariaRequired}
          aria-describedby={ariaDescribedBy}
          aria-expanded={open}
          disabled={disabled}
          className={cn('h-11 w-full justify-between font-mono sm:w-64', className)}
        >
          {value ? (
            <span className="flex min-w-0 items-center gap-2">
              <span className="shrink-0">{value.code}</span>
              {value.label && (
                <>
                  <span aria-hidden="true" className="text-muted-foreground">
                    ·
                  </span>
                  <span className="truncate font-sans text-muted-foreground">{value.label}</span>
                </>
              )}
            </span>
          ) : (
            <span className="truncate text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <Command shouldFilter={false}>
          <CommandInput placeholder={placeholder} value={search} onValueChange={setSearch} />
          <CommandList>
            {allowNoSize && !term && (
              <CommandGroup>
                <CommandItem value={PRINT_SIZE_NO_SIZE_KEY} onSelect={() => commit(PRINT_SIZE_NO_SIZE_KEY)}>
                  <span className="italic text-muted-foreground">{noSizeLabel}</span>
                </CommandItem>
              </CommandGroup>
            )}

            {filtered.length === 0 && !canCreate && <CommandEmpty>{emptyLabel}</CommandEmpty>}

            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((opt) => (
                  <CommandItem
                    key={opt.code}
                    value={opt.code}
                    disabled={opt.disabled}
                    onSelect={() => commit(opt.code)}
                    className="gap-2"
                  >
                    <span className="shrink-0 font-mono text-xs">{opt.code}</span>
                    {/* A code with no name shows the code alone, never a stray separator. */}
                    {opt.label ? (
                      <>
                        <span aria-hidden="true">·</span>
                        <span className="min-w-0 flex-1 truncate text-muted-foreground">{opt.label}</span>
                      </>
                    ) : (
                      <span className="flex-1" />
                    )}
                    {opt.badges?.map((badge) => (
                      <Badge key={badge} variant="muted" className="shrink-0">
                        {badge}
                      </Badge>
                    ))}
                    {opt.disabled && opt.disabledReason && (
                      <span className="shrink-0 text-xs text-muted-foreground">{opt.disabledReason}</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {canCreate && (
              <CommandGroup>
                <CommandItem value={`__create__${termCode}`} onSelect={() => commit(termCode)}>
                  <Plus className="mr-2 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {createLabel(termCode)}
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
